// Bring your numbers and the port-out PIN (DESIGN.md "Bring your numbers" and "Port-out PIN";
// spec docs/specs/telephony-compliance.md sections 2.2 to 2.8). The JSON routes go through the
// shared typed client (packages/api-client, `portsAdmin`); the bill upload (multipart) and the
// letter of authorization (a PDF with its SHA-256 in a header) use the console's own fetch.
import { queryOptions } from "@tanstack/react-query";
import { portsAdmin } from "@vd/api-client";
import { API_BASE } from "../config";
import { ApiError, api } from "./client";
import { transport } from "./transport";
import type { ApiErrorBody } from "./types";

export type CheckView = portsAdmin.CheckView;
export type CheckGroup = portsAdmin.CheckGroup;
export type ExcludedNumber = portsAdmin.ExcludedNumber;
export type Port = portsAdmin.Port;
export type PortEdit = portsAdmin.PortEdit;
export type PortNumber = portsAdmin.PortNumber;
export type PortState = portsAdmin.PortState;
export type PortOutSettings = portsAdmin.PortOutSettings;

export const ports = new portsAdmin.PortsAdminApi(transport);

export const portKeys = {
  list: ["admin", "ports", "list"] as const,
  port: (id: string) => ["admin", "ports", "port", id] as const,
  portOut: ["admin", "port-out"] as const,
};

export const portsQuery = queryOptions({
  queryKey: portKeys.list,
  queryFn: async ({ signal }) => (await ports.list(signal)).ports,
});

export const portQuery = (id: string) =>
  queryOptions({
    queryKey: portKeys.port(id),
    queryFn: ({ signal }) => ports.get(id, signal),
  });

export const portOutQuery = queryOptions({
  queryKey: portKeys.portOut,
  queryFn: ({ signal }) => ports.portOut(signal),
});

/** Uploads the bill copy; answers the port. 413 `too_large`. */
export function uploadBill(id: string, file: File): Promise<Port> {
  const form = new FormData();
  form.append("file", file, file.name);
  return api<Port>(portsAdmin.portBillPath(id), { method: "POST", form });
}

/** The unsigned letter of authorization and the SHA-256 the signature binds to. */
export interface Loa {
  pdf: Blob;
  sha256: string | null;
}

export async function fetchLoa(id: string, signal?: AbortSignal): Promise<Loa> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${portsAdmin.portLoaPath(id)}`, {
      credentials: "include",
      headers: { accept: "application/pdf" },
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Can't reach the server. Check your connection and try again.");
  }
  if (!res.ok) {
    let code = "http_error";
    let message = `The server returned ${res.status}.`;
    let data: unknown;
    try {
      const b = (await res.json()) as ApiErrorBody;
      if (b?.error) {
        code = b.error.code;
        message = b.error.message;
        data = b.error.data;
      }
    } catch {
      // Not JSON; keep the generic words.
    }
    throw new ApiError(res.status, code, message, undefined, data);
  }
  return { pdf: await res.blob(), sha256: res.headers.get("x-document-sha256") };
}
